<template>
    <div class="qr-issue-history-card mb-3">
        <!-- Header Start -->
        <div class="issue-toolbar">
            <div class="issue-toolbar-main">
                <h3 class="issue-toolbar-title">Riwayat Pengiriman</h3>

                <div class="issue-filters">
                    <!-- Dropdown Dealer -->
                    <div class="issue-filter">
                        <MultiSelect
                            v-model="selectedDealer"
                            :options="dealerOptions"
                            track-by="code"
                            :custom-label="formatOptionLabel"
                            class="qr-issue-select"
                            :class="{ 'qr-issue-select--active': !!selectedDealer }"
                            placeholder="Cari dealer..."
                            select-label=""
                            deselect-label=""
                            selected-label=""
                            :allow-empty="true"
                        >
                            <template slot="placeholder">All Dealer</template>
                            <template slot="noResult">Dealer tidak ditemukan</template>
                        </MultiSelect>
                    </div>

                    <!-- Dropdown POS (cascading, hanya tab Dealer → POS) -->
                    <div
                        v-if="flow === FLOW_DEALER_POS"
                        class="issue-filter"
                        :class="{ 'issue-filter--tooltip': !selectedDealer }"
                        :data-tooltip="selectedDealer ? null : 'Pilih dealer terlebih dahulu'"
                    >
                        <MultiSelect
                            v-model="selectedPos"
                            :options="posOptions"
                            track-by="code"
                            :custom-label="formatOptionLabel"
                            class="qr-issue-select"
                            :class="{ 'qr-issue-select--active': !!selectedPos }"
                            placeholder="Cari POS..."
                            select-label=""
                            deselect-label=""
                            selected-label=""
                            :disabled="!selectedDealer"
                            :loading="isLoadingPos"
                            :allow-empty="true"
                        >
                            <template slot="placeholder">All POS</template>
                            <template slot="noResult">POS tidak ditemukan</template>
                            <template slot="noOptions">Tidak ada POS</template>
                        </MultiSelect>
                    </div>

                    <!-- Dropdown Status -->
                    <div class="issue-filter">
                        <MultiSelect
                            v-model="filter.status"
                            :options="STATUS_OPTIONS"
                            :custom-label="formatStatusLabel"
                            class="qr-issue-select"
                            :class="{ 'qr-issue-select--active': !!filter.status }"
                            placeholder="All Status"
                            :searchable="false"
                            select-label=""
                            deselect-label=""
                            selected-label=""
                            :allow-empty="true"
                        ></MultiSelect>
                    </div>
                </div>
            </div>

            <!-- Kanan: tombol aksi -->
            <div class="issue-toolbar-actions">
                <button
                    type="button"
                    class="btn-download-xls"
                    :disabled="isDownloading"
                    @click="downloadAll"
                >
                    Download XLS
                    <i class="fas fa-file-download"></i>
                </button>

                <button
                    v-if="!isUserSE"
                    type="button"
                    class="btn-buat-delivery"
                    @click="$emit('showMetodeModal')"
                >
                    Buat Delivery
                </button>
            </div>
        </div>

        <!-- Tabs Alur Pengiriman -->
        <div class="flow-tabs" role="tablist">
            <button
                v-for="tab in tabs"
                :key="tab.value"
                type="button"
                role="tab"
                class="flow-tab"
                :class="{ 'flow-tab--active': flow === tab.value }"
                :aria-selected="flow === tab.value ? 'true' : 'false'"
                @click="handleTabChange(tab.value)"
            >
                {{ tab.label }}
            </button>
        </div>
        <!-- Header End -->

        <!-- Table Start (Scrollable Left-Right) -->
        <div class="w-100 overflow-auto table-scroll-container">
            <table class="table w-100 qtrust-table" style="min-width: 1400px;">
                <thead>
                    <tr>
                        <th
                            v-for="column in columns"
                            :key="column.key"
                            :class="{ clickHover: !!column.sort }"
                            @click="column.sort && sortClick(column.sort)"
                        >
                            {{ column.label }}
                            <i v-if="column.sort" class="fas" :class="sortIcon(column.sort)"></i>
                        </th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in rows" :key="row.id">
                        <td v-for="column in columns" :key="column.key">
                            <a
                                v-if="column.key === 'bastNo' && row.bastNo"
                                href="#"
                                class="bast-link"
                                @click.prevent="$emit('previewBast', row)"
                            >{{ row.bastNo }}</a>
                            <template v-else>{{ formatCell(row, column) }}</template>
                        </td>
                        <td>
                            <div class="d-flex justify-content-between">
                                <template v-if="canEditRow(row)">
                                    <i class="fa fa-edit text-primary ml-2 clickHover" title="Edit" @click="$emit('editData', row.raw)"></i>
                                    <i class="fa fa-trash text-danger ml-2 clickHover" title="Hapus" @click="deleteQRIssue(row.id)"></i>
                                </template>
                                <i class="fa fa-file-download ml-2 clickHover" title="Preview BAST" @click="$emit('previewBast', row)"></i>
                            </div>
                        </td>
                    </tr>
                    <tr v-if="!isLoading && rows.length === 0">
                        <td :colspan="columns.length + 1" class="text-center text-muted py-4">
                            Belum ada pengiriman pada filter ini
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <!-- Table End -->

        <div class="d-flex justify-content-end align-items-center py-2 px-3" v-if="rows.length">
            <p class="text-muted mb-0" style="font-size: 13px;">Showing {{ (config.page-1)*config.limit+1 }}-{{ (config.page-1)*config.limit+rows.length }} of {{ config.total }} Items</p>
            <div class="d-flex align-items-center text-muted ml-3">
                <button class="btn btn-link px-1" style="color: #627884" :disabled="config.page <= 1" @click="goToPage(1)"><i class="fa fa-angle-double-left"></i></button>
                <button class="btn btn-link px-1" style="color: #627884" :disabled="config.page <= 1" @click="goToPage(config.page - 1)"><i class="fa fa-angle-left"></i></button>
                <p class="px-2 text-dark mb-0" style="font-size: 13px;">{{ config.page }}</p>
                <button class="btn btn-link px-1" style="color: #627884" :disabled="config.page >= config.lastPage" @click="goToPage(config.page + 1)"><i class="fa fa-angle-right"></i></button>
                <button class="btn btn-link px-1" style="color: #627884" :disabled="config.page >= config.lastPage" @click="goToPage(config.lastPage)"><i class="fa fa-angle-double-right"></i></button>
            </div>
        </div>
    </div>
</template>

<script>
    import moment from 'moment';
    import * as XLSX from 'xlsx';
    import {
        AUDIENCE_DEFAULT,
        AUDIENCE_SE,
        FLOW_DEALER_POS,
        FLOW_MD_DEALER,
        fetchIssuePage,
        fetchPosOptions,
        formatCount,
        issueSortKey,
        toDealerOptions,
    } from '@/services/qr-issue';

    const STATUS_OPTIONS = ['INTRANSIT', 'RECEIVED'];
    const DATE_TIME_FORMAT = 'DD MMM YYYY | HH:mm';
    const DEFAULT_SORT_BY = 'delivery_at';
    const DEFAULT_SORT_ORDER = 'DESC';

    const POS_COLUMNS = [
        { key: 'posCode', label: 'Kode POS' },
        { key: 'posName', label: 'Nama POS' },
    ];

    function buildColumns(flow) {
        return [
            { key: 'deliveredAt', label: 'Tanggal Pengiriman', type: 'datetime' },
            { key: 'qty', label: 'Jumlah QR', type: 'count' },
            { key: 'ahmId', label: 'ID AHM' },
            { key: 'dealerCode', label: 'Kode Dealer' },
            { key: 'dealerName', label: 'Nama Dealer' },
            ...(flow === FLOW_DEALER_POS ? POS_COLUMNS : []),
            { key: 'city', label: 'Kabupaten' },
            { key: 'dealerType', label: 'Status Jaringan' },
            { key: 'status', label: 'Status', type: 'status' },
            { key: 'bastNo', label: 'Kode BAST' },
            { key: 'senderName', label: 'Nama Pengirim' },
            { key: 'receivedAt', label: 'Tanggal Diterima', type: 'datetime' },
        ];
    }

    export default {
        props : {
            startDate : { type: String, default: null },
            endDate : { type: String, default: null }
        },
        data () {
            return {
                FLOW_DEALER_POS,
                STATUS_OPTIONS,
                tabs : [
                    { value: FLOW_MD_DEALER, label: 'MD → Dealer' },
                    { value: FLOW_DEALER_POS, label: 'Dealer → POS' },
                ],
                flow : FLOW_MD_DEALER,
                selectedDealer : null,
                selectedPos : null,
                posOptions : [],
                isLoadingPos : false,
                rows : [],
                isLoading : false,
                isDownloading : false,
                requestId : 0,
                filter : {
                    status : null,
                },
                config : {
                    page: 1,
                    limit: 15,
                    lastPage: 0,
                    total: 0,
                    sort_by: DEFAULT_SORT_BY,
                    sort_order: DEFAULT_SORT_ORDER
                },
            }
        },
        computed: {
            isUserSE () {
                return this.$store.state.userData.userData.role === 'USER SE';
            },
            dealerOptions () {
                const dealerList = this.$store.getters.getDealer ? this.$store.getters.getDealer() : [];
                return toDealerOptions(dealerList);
            },
            audience () {
                return this.isUserSE ? AUDIENCE_SE : AUDIENCE_DEFAULT;
            },
            columns () {
                return buildColumns(this.flow).map(column => ({
                    ...column,
                    sort: issueSortKey(this.audience, this.flow, column.key),
                }));
            },
            query () {
                return {
                    flow: this.flow,
                    startDate: this.startDate,
                    endDate: this.endDate,
                    dealerCode: this.selectedDealer ? this.selectedDealer.code : null,
                    posCode: this.selectedPos ? this.selectedPos.code : null,
                    status: this.filter.status,
                };
            },
            queryKey () {
                return JSON.stringify(this.query);
            },
        },
        watch : {
            selectedDealer (dealer) {
                this.selectedPos = null;
                this.posOptions = [];
                if (dealer && this.flow === FLOW_DEALER_POS) this.loadPosOptions(dealer.code);
            },
            queryKey () {
                this.config.page = 1;
                this.generateDatas();
                this.$emit('filterChange', { ...this.query });
            },
            'config.page' : 'generateDatas'
        },
        mounted () {
            this.generateDatas();
            this.$emit('filterChange', { ...this.query });
        },
        methods : {
            formatOptionLabel (option) {
                return option.name ? `${option.code} – ${option.name}` : option.code;
            },
            formatStatusLabel (status) {
                if (status === 'INTRANSIT') return 'In-Transit';
                if (status === 'RECEIVED') return 'Received';
                return status;
            },
            formatCell (row, column) {
                const value = row[column.key];
                if (column.type === 'datetime') return value ? moment(value).format(DATE_TIME_FORMAT) : '-';
                if (column.type === 'count') return formatCount(value);
                if (column.type === 'status') return value ? this.formatStatusLabel(value) : '-';
                return value || '-';
            },
            canEditRow (row) {
                return !this.isUserSE && this.flow === FLOW_MD_DEALER && row.status === 'INTRANSIT';
            },
            handleTabChange (flow) {
                if (this.flow === flow) return;
                this.flow = flow;
                const isSortable = this.columns.some(column => column.sort === this.config.sort_by);
                if (!isSortable) {
                    this.config.sort_by = DEFAULT_SORT_BY;
                    this.config.sort_order = DEFAULT_SORT_ORDER;
                }
                this.selectedPos = null;
                this.posOptions = [];
                if (flow === FLOW_DEALER_POS && this.selectedDealer) {
                    this.loadPosOptions(this.selectedDealer.code);
                }
            },
            async loadPosOptions (dealerCode) {
                this.isLoadingPos = true;
                try {
                    const options = await fetchPosOptions(this.$http, dealerCode);
                    if (this.selectedDealer && this.selectedDealer.code === dealerCode) {
                        this.posOptions = options;
                    }
                } catch (err) {
                    console.log(err);
                    this.posOptions = [];
                } finally {
                    this.isLoadingPos = false;
                }
            },
            goToPage (page) {
                if (page >= 1 && page <= this.config.lastPage) this.config.page = page;
            },
            sortIcon (sortBy) {
                if (this.config.sort_by !== sortBy) return 'fa-sort';
                return this.config.sort_order === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
            },
            sortClick (sortBy) {
                if (this.config.sort_by === sortBy) {
                    this.config.sort_order = this.config.sort_order === 'asc' ? 'desc' : 'asc';
                } else {
                    this.config.sort_by = sortBy;
                    this.config.sort_order = 'desc';
                }
                this.generateDatas();
            },
            async generateDatas () {
                const requestId = ++this.requestId;
                this.isLoading = true;
                try {
                    const page = await fetchIssuePage(this.$http, {
                        ...this.query,
                        audience: this.audience,
                        page: this.config.page,
                        limit: this.config.limit,
                        sortBy: this.config.sort_by,
                        sortOrder: this.config.sort_order,
                    });
                    if (requestId !== this.requestId) return;
                    this.rows = page.rows;
                    this.config.lastPage = page.lastPage;
                    this.config.total = page.total;
                } catch (err) {
                    // Dealer → POS is not served yet; show the empty state.
                    console.log(err);
                    if (requestId !== this.requestId) return;
                    this.rows = [];
                    this.config.lastPage = 0;
                    this.config.total = 0;
                } finally {
                    if (requestId === this.requestId) this.isLoading = false;
                }
            },
            deleteQRIssue (issueId) {
                this.$swal.fire({
                    html: `<div class="text-center my-2" style="color: #000000;"><h4>Apakah Anda yakin?</h4>
                        <div>Data yang telah dihapus tidak dapat dikembalikan lagi</div>
                        </div>`,
                    type : 'warning',
                    showCancelButton: true,
                    cancelButtonText: `Batal`,
                    confirmButtonText: `Hapus`,
                    reverseButtons: true,
                    customClass: {
                        confirmButton: "btn btn-danger btn-lg mx-2",
                        cancelButton: "btn btn-outline btn-outline-danger btn-lg mx-2"
                    },
                    buttonsStyling: false
                }).then((result) => {
                    if (result.value) {
                        this.$http.delete(`/api/qrs/issue/${issueId}`)
                        .then(() => {
                            this.generateDatas();
                        })
                        .catch((err) => {
                            console.log(err);
                        });
                    }
                });
            },
            async downloadAll () {
                if (!this.config.total) return;
                this.isDownloading = true;
                try {
                    const { rows } = await fetchIssuePage(this.$http, {
                        ...this.query,
                        audience: this.audience,
                        page: 1,
                        limit: this.config.total,
                        sortBy: this.config.sort_by,
                        sortOrder: this.config.sort_order,
                    });
                    const sheetRows = rows.map(row => {
                        const sheetRow = {};
                        this.columns.forEach(column => {
                            sheetRow[column.label] = column.type === 'count'
                                ? row[column.key]
                                : this.formatCell(row, column);
                        });
                        return sheetRow;
                    });

                    const worksheet = XLSX.utils.json_to_sheet(sheetRows, {
                        header: this.columns.map(column => column.label)
                    });
                    const workbook = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
                    const timestamp = moment().format('DDMMYYYYHHmmss');
                    const fileSuffix = `${this.flow}_${this.startDate}_${this.endDate}`;
                    XLSX.writeFile(workbook, `qr_issue_${fileSuffix}_${timestamp}.xlsx`);
                } catch (error) {
                    console.error('Error fetching data or exporting XLSX:', error);
                } finally {
                    this.isDownloading = false;
                }
            },
        },
    }
</script>


<style>
    /* MultiSelect renders its own DOM, so these cannot be scoped.
       Everything hangs off .qr-issue-history-card so nothing leaks to other pages. */
    .qr-issue-history-card .qr-issue-select.multiselect {
        min-height: 0;
        font-size: 12px;
        color: #2F2F2F;
    }

    .qr-issue-history-card .qr-issue-select .multiselect__tags {
        display: flex;
        align-items: center;
        min-height: 32px;
        height: 32px;
        padding: 0 32px 0 12px;
        background-color: #FFFFFF;
        border: 1px solid #F3F3F3;
        border-radius: 4px;
    }

    .qr-issue-history-card .qr-issue-select .multiselect__placeholder,
    .qr-issue-history-card .qr-issue-select .multiselect__single,
    .qr-issue-history-card .qr-issue-select .multiselect__input {
        min-height: 0;
        margin: 0;
        padding: 0;
        font-size: 12px;
        line-height: 30px;
        color: #2F2F2F;
        background: transparent;
    }

    .qr-issue-history-card .qr-issue-select .multiselect__single {
        display: block;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .qr-issue-history-card .qr-issue-select .multiselect__input {
        width: 100%;
    }

    .qr-issue-history-card .qr-issue-select .multiselect__select {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 30px;
        padding: 0;
        line-height: 1;
    }

    .qr-issue-history-card .qr-issue-select .multiselect__select::before {
        position: static;
        z-index: 1;
        margin: 0;
        border-width: 6px 4px 0;
        border-color: #000000 transparent transparent;
    }

    .qr-issue-history-card .qr-issue-select .multiselect__content-wrapper {
        min-width: 100%;
        width: max-content;
        max-width: 280px;
        border-color: #F3F3F3;
        z-index: 20;
    }

    .qr-issue-history-card .qr-issue-select .multiselect__option {
        min-height: 0;
        padding: 9px 12px;
        font-size: 12px;
        line-height: 14px;
    }

    .qr-issue-history-card .qr-issue-select.qr-issue-select--active .multiselect__tags {
        border-color: #F36F21;
    }

    .qr-issue-history-card .qr-issue-select.multiselect--disabled {
        opacity: 1;
        background: transparent;
    }

    .qr-issue-history-card .qr-issue-select.multiselect--disabled .multiselect__tags,
    .qr-issue-history-card .qr-issue-select.multiselect--disabled .multiselect__select {
        background-color: #F8F8F8;
        cursor: not-allowed;
    }

    .qr-issue-history-card .qr-issue-select.multiselect--disabled .multiselect__placeholder {
        color: #B2B2B2;
    }

    .qr-issue-history-card .qr-issue-select.multiselect--disabled .multiselect__select::before {
        border-top-color: #B2B2B2;
    }
</style>

<style scoped>
    .qr-issue-history-card {
        background-color: #FFFFFF;
        border-radius: 8px;
    }

    /* Toolbar: title + filters on the left, actions on the right */
    .issue-toolbar {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 12px 16px;
        padding: 20px 24px 16px;
    }

    .issue-toolbar-main {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 12px 24px;
    }

    .issue-toolbar-title {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        color: #2F2F2F;
        white-space: nowrap;
    }

    .issue-filters {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 12px 16px;
    }

    .issue-filter {
        position: relative;
        width: 151px;
        white-space: nowrap;
    }

    /* CSS tooltip for the disabled POS dropdown */
    .issue-filter--tooltip::after {
        content: attr(data-tooltip);
        position: absolute;
        top: calc(100% + 8px);
        left: 0;
        z-index: 30;
        padding: 8px 12px;
        background-color: rgba(47, 47, 47, 0.95);
        border-radius: 4px;
        font-size: 12px;
        line-height: 1.2;
        color: #FFFFFF;
        white-space: nowrap;
        pointer-events: none;
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.15s ease;
    }

    .issue-filter--tooltip:hover::after {
        opacity: 1;
        visibility: visible;
    }

    .issue-toolbar-actions {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 12px;
    }

    .btn-download-xls {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        height: 32px;
        padding: 0 24px;
        background-color: #FFFFFF;
        border: 1px solid #F3F3F3;
        border-radius: 4px;
        font-size: 12px;
        color: #2F2F2F;
        white-space: nowrap;
        cursor: pointer;
    }

    .btn-download-xls:hover:not(:disabled) {
        background-color: #FAFAFA;
    }

    .btn-download-xls:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .btn-buat-delivery {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: 32px;
        padding: 0 18px;
        background-color: #F36F21;
        border: none;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 500;
        color: #FFFFFF;
        white-space: nowrap;
        cursor: pointer;
    }

    .btn-buat-delivery:hover {
        background-color: #E06216;
        color: #FFFFFF;
    }

    /* Tabs */
    .flow-tabs {
        display: flex;
        gap: 24px;
        padding: 0 24px;
        border-bottom: 1px solid #F3F3F3;
    }

    .flow-tab {
        padding: 0 0 10px;
        background: transparent;
        border: none;
        border-bottom: 2px solid transparent;
        border-radius: 0;
        font-size: 12px;
        font-weight: 400;
        color: #B2B2B2;
        cursor: pointer;
    }

    .flow-tab:hover {
        color: #707070;
    }

    .flow-tab--active,
    .flow-tab--active:hover {
        font-weight: 600;
        color: #F36F21;
        border-bottom-color: #F36F21;
    }

    /* Table */
    .table-scroll-container {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
    }

    .table-scroll-container::-webkit-scrollbar {
        height: 4px;
    }

    .table-scroll-container::-webkit-scrollbar-track {
        background: transparent;
    }

    .table-scroll-container::-webkit-scrollbar-thumb {
        background: #D9D9D9;
        border-radius: 50px;
    }

    .table-scroll-container::-webkit-scrollbar-thumb:hover {
        background: #B2B2B2;
    }

    .clickHover:hover {
        cursor: pointer;
    }

    .qtrust-table {
        margin-bottom: 0;
    }

    .qtrust-table > thead > tr > th {
        text-align: left;
        border-top: none;
        border-bottom: 1px solid #F3F3F3;
        padding: 12px;
        height: 40px;
        vertical-align: middle;
        font-size: 12px;
        font-weight: 600;
        color: #2F2F2F;
        white-space: nowrap;
    }

    .qtrust-table > thead > tr > th > i {
        margin-left: 4px;
        font-size: 10px;
        color: #D1D1D1;
    }

    .qtrust-table > thead > tr > th > i.fa-sort-up,
    .qtrust-table > thead > tr > th > i.fa-sort-down {
        color: #2F2F2F;
    }

    .qtrust-table > tbody > tr > td {
        text-align: left;
        padding: 12px;
        border-top: none;
        border-bottom: 1px solid #F3F3F3;
        height: 40px;
        vertical-align: middle;
        font-size: 12px;
        color: #2F2F2F;
        white-space: nowrap;
    }

    .qtrust-table > thead > tr > th:first-child,
    .qtrust-table > tbody > tr > td:first-child {
        padding-left: 24px;
    }

    .qtrust-table > thead > tr > th:last-child,
    .qtrust-table > tbody > tr > td:last-child {
        padding-right: 24px;
    }

    /* Kode BAST: plain text that still opens the preview */
    .bast-link {
        color: inherit;
        text-decoration: none;
        cursor: pointer;
    }

    .bast-link:hover {
        color: inherit;
        text-decoration: underline;
    }

    .bast-link:focus-visible {
        outline: 2px solid #F36F21;
        outline-offset: 2px;
        border-radius: 2px;
    }
</style>
